import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import {
    EnumInput,
    SortDirection,
    TransferPayerInput,
    TransfersInput,
    TransfersResponse
} from '../../model/models';

import { BuyerPaymentsListResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/list-payments';
import { mapBuyerTransferSortColumn } from '../../helpers/sort-column-mapping';
import { _mapTransferTypeToPayerAndPaymentType } from '../../helpers/transfer-type-mapping';

export class BuyerPaymentsListRequest extends Mk2Request {
    buyerPaymentListPayerEnums?: 'BUYER' | 'DEPOSIT_ACCOUNT' | 'LSQ' | 'UNKNOWN_PAYER'[];
    dateRangeEnd?: string;
    dateRangeStart?: string;
    pagination?: Pagination;
    searchValue?: string;
    searchInvoiceNumber?: string;

    constructor(initial?: Partial<BuyerPaymentsListRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class ListPaymentsService extends ApiService<
    BuyerPaymentsListRequest,
    BuyerPaymentsListResponse
> {
    public requestType = BuyerPaymentsListRequest;
    public responseType = BuyerPaymentsListResponse;
    public endpointName = 'buyer/BuyerPaymentsList/ListPayments';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'buyer/BuyerPaymentsList/ListPayments';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: BuyerPaymentsListRequest
    ): { query: string } | BuyerPaymentsListRequest {
        if (this._isPayLaterEnabled) {
            const payers: EnumInput<TransferPayerInput>[] = [];
            (req.buyerPaymentListPayerEnums as string[])?.forEach((payer) => {
                if (payer === 'LSQ') {
                    payers.push(new EnumInput(TransferPayerInput.LSQ));
                } else if (payer === 'BUYER') {
                    payers.push(new EnumInput(TransferPayerInput.BUYER));
                }
            });

            const input: TransfersInput = {
                invoiceNumber: req.searchInvoiceNumber.trim(),
                amountOrTraceNumberSearch: req.searchValue,
                payer: payers,
                dateRange: {
                    startDate: req.dateRangeStart,
                    endDate: req.dateRangeEnd
                },
                pagination: {
                    skip: req.pagination.skip,
                    take: req.pagination.take,
                    sortColumn: new EnumType(mapBuyerTransferSortColumn(req.pagination.sortColumn)),
                    sortDirection: new EnumType(
                        (req.pagination.sortDirection ?? (null as unknown)) as SortDirection
                    )
                }
            };
            const query = jsonToGraphQLQuery({
                transfers: {
                    __args: { input },
                    rows: {
                        id: true,
                        totalApprovedInvoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        buyerPaymentAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        recipientType: true,
                        transferDate: true,
                        transferType: true,
                        traceNumber: true,
                        invoiceCount: true
                    },
                    totals: {
                        transferCount: true
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    public responseMapper(
        res: GraphQLResponse<TransfersResponse> | BuyerPaymentsListResponse
    ): BuyerPaymentsListResponse {
        if (this._isPayLaterEnabled) {
            const { transfers } = (res as GraphQLResponse<TransfersResponse>).data;
            return {
                // TODO: Might need to map sortColumn to whatever the UI expects
                rows: transfers.rows.map((transfer) => {
                    const [payer, paymentType] = _mapTransferTypeToPayerAndPaymentType(
                        transfer.transferType
                    );

                    return {
                        batchKey: transfer.id,
                        numberOfInvoices: transfer.invoiceCount,
                        payer,
                        paymentAmount: transfer.buyerPaymentAmount,
                        paymentDate: transfer.transferDate,
                        paymentType,
                        referenceNumber: transfer.traceNumber,
                        recipient: transfer.recipientType
                    };
                }),
                totalCount: transfers.totals.transferCount
                // ignoring searchValue, seems to not be in use
            } as BuyerPaymentsListResponse;
        }
        return res as BuyerPaymentsListResponse;
    }
}
